import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Modal,
  Segmented,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useGlobalSettings } from '../../../hooks/useGlobalSettings';
import { useRoadsAndSewersTotalSync } from '../../../hooks/useRoadsAndSewersTotalSync';
import {
  buildRunMatrix,
  RUN_MATRIX_DISCIPLINE_LABEL,
  type RunMatrixDiscipline,
} from '../../../utils/runMatrixGenerator';
import {
  buildSiteWorksMatrices,
  type SectionMatrix,
} from '../../../utils/siteWorksMatrixGenerator';
import { emptySpoilUserInputs } from '../../../utils/spoilReconciliationSummary';
import { exportRunMatrixToExcel } from '../../../utils/runMatrixExcelExport';
import { buildRoadsAndSewersSumAnalysisRows } from '../../../utils/roadsAndSewersSumAnalysisRows';
import {
  resolveRunMatrixMappedItems,
  applyRunMatrixMappingToDrainage,
  applyRunMatrixMappingToSection,
} from '../../../utils/dynamicBillSummaryRunMatrix';
import DrainageRunMatrix from './DrainageRunMatrix';
import SectionMatrixTable from './SectionMatrixTable';

const { Text, Title, Paragraph } = Typography;

// Feature flag, explicit opt-in: only .env.development (DOGMA QA) ships it on,
// so "Export to Excel" stays hidden on QA, UAT and production.
const showRnsExports = import.meta.env.VITE_SHOW_RNS_EXPORTS === 'true';

const fmtCurrency = (n: number): string =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type ActiveSection = 'drainage' | SectionMatrix['key'];

/**
 * Roads & Sewers "Run Matrix" tab — the counterpart of Groundworks' Plot Matrix.
 *
 * One matrix per section of the tender, each with the unit of measure that
 * section is actually taken off by:
 *
 *   Drainage Runs  — one row per manhole-to-manhole pipe run (Foul + Storm),
 *                    with a per-discipline sub-tab for the Foul build-ups
 *                    (Pump Station, Rising Main) and the Storm build-ups
 *                    (Ponds & Swales, Cellular / Weholite / Conc attenuation,
 *                    Box Culverts) — mirroring how the Foul and Storm Bill
 *                    Summary tabs nest their own build-up tabs
 *   Preliminaries  — one row per bill group, columns by charge type
 *   Roads          — one row per road
 *   Main Services  — one row per road
 *   Substation     — one row per element (base / access)
 *   POS            — one row per public open space
 *   Compound       — one row per compound element
 *   Spoil          — one row per area (volumes, not money)
 *
 * This component owns what is shared across them: the section switcher, the
 * markup indicators (mirrored read-only from Sum Analysis, same contract the
 * Plot Matrix has with its own Sum Analysis), the Excel export and the
 * allocation-rules modal. The grids
 * themselves live in `DrainageRunMatrix` and `SectionMatrixTable`; the numbers
 * come from `runMatrixGenerator.ts` / `siteWorksMatrixGenerator.ts`, both of
 * which re-use each section's own Bill Summary maths rather than repeating it.
 */
const RunMatrix: React.FC = () => {
  const { state } = useTender();
  const showDevMode = useDevMode();
  const { settings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } = useItemLibrary(
    state.region || '',
  );

  const [activeSection, setActiveSection] = useState<ActiveSection>('drainage');
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const ohpPercent = settings.roadsAndSewers?.overheadAndProfit ?? 0;
  const preliminariesPercent = settings.roadsAndSewers?.preliminaries ?? 0;

  // ── Markup, mirrored from Sum Analysis ────────────────────────────────────
  // Same contract as the Groundworks Plot Matrix: Sum Analysis owns the
  // OH&P / Preliminaries checkboxes and writes them to localStorage; the matrix
  // mirrors them read-only so its cells can never disagree with the tender
  // total shown on that tab. `getCheckboxState` matches the Plot Matrix helper —
  // honour the saved value if there is one, else default to "on when the
  // percentage is non-zero".
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      try {
        return JSON.parse(saved) as boolean;
      } catch {
        return fallbackPercent > 0;
      }
    }
    return fallbackPercent > 0;
  };

  const [includeOhp, setIncludeOhp] = useState(() =>
    getCheckboxState('roadsAndSewers_includeOhpDisplay', ohpPercent),
  );
  const [includePreliminaries, setIncludePreliminaries] = useState(() =>
    getCheckboxState('roadsAndSewers_includePreliminariesDisplay', preliminariesPercent),
  );

  // Poll so a toggle flipped on Sum Analysis propagates here without a refresh —
  // 500ms, the same interval the Plot Matrix and the historic-version flag use.
  useEffect(() => {
    const check = () => {
      const nextOhp = getCheckboxState('roadsAndSewers_includeOhpDisplay', ohpPercent);
      const nextPrelims = getCheckboxState(
        'roadsAndSewers_includePreliminariesDisplay',
        preliminariesPercent,
      );
      setIncludeOhp((prev) => (prev === nextOhp ? prev : nextOhp));
      setIncludePreliminaries((prev) => (prev === nextPrelims ? prev : nextPrelims));
    };
    check();
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, [ohpPercent, preliminariesPercent]);

  // ── Run Matrix Total — the Sum Analysis GRAND TOTAL, deliberately before
  // discount ───────────────────────────────────────────────────────────────
  // Rather than re-summing every section matrix's own grandTotal (which would
  // have to duplicate Sum Analysis's dynamic-tabs/extra-items handling to
  // ever tie out, and drifted exactly that way before this fix), this calls
  // the same hook `RsSumAnalysisBillSummary.tsx` renders from, with the same
  // OH&P/Preliminaries options, so the banner below is the Sum Analysis
  // GRAND TOTAL by construction — not a second calculation that merely tries
  // to match it. Reads `grandTotal`, not `finalTotal`/`discountAmount` — a
  // discount is a tender-level commercial adjustment applied at the very end,
  // not something that belongs to any run/road/element this matrix allocates
  // cost against, so the Run Matrix Total must never move when the Sum
  // Analysis discount checkbox is toggled. `includeDiscountDisplay` is
  // deliberately not passed (it only affects `discountAmount`/`finalTotal`,
  // which the `grandTotal` destructure below ignores) — no discount-mirroring
  // state needed for the Run Matrix Total banner. The full return value is
  // also kept (`sumAnalysisTotals`) for the Excel export's "Summary" sheet,
  // which — unlike the banner — needs the full Sum Analysis Bill Summary
  // (every domain, dynamic tabs, discount, TENDER TOTAL).
  const sumAnalysisTotals = useRoadsAndSewersTotalSync(
    itemLibraryData,
    isLoadingItemLibrary,
    {
      includeOhpDisplay: includeOhp,
      includePreliminariesDisplay: includePreliminaries,
    },
  );
  const { grandTotal: sumAnalysisGrandTotal } = sumAnalysisTotals;

  const markupFactor = useMemo(
    () =>
      (1 + (includeOhp ? ohpPercent : 0) / 100) *
      (1 + (includePreliminaries ? preliminariesPercent : 0) / 100),
    [includeOhp, includePreliminaries, ohpPercent, preliminariesPercent],
  );

  // Keyed by the item library's raw, as-received resourceCode — deliberately
  // NOT trimmed/upper-cased here. Most Bill Summaries this matrix must reconcile
  // against (`FoulSewersBillSummary.tsx`, `SubstationBillSummary.tsx`,
  // `POSBillSummary.tsx`, `CompoundBillSummary.tsx`, the Storm equivalents)
  // build their own rateMap the same raw way and look codes up with an exact
  // match, no fallback — so normalizing this shared key would resolve rates for
  // codes those bills treat as unpriced, inflating the matrix's total *above*
  // theirs rather than tying to them.
  //
  // Roads, Main Services and Preliminaries are the exceptions — their own Bill
  // Summaries (`RoadsBillSummary.tsx`'s `resourceCodeMap`,
  // `MainServicesBillSummary.tsx`'s `rateMap`, `PrelimsBillSummary.tsx`'s
  // `resourceCodeMap`) DO normalize both sides of the lookup — so
  // `buildRoadsMatrix`/`buildMainServicesMatrix`/`buildPrelimsMatrix` each build
  // their own normalized copy of this raw `rateMap` from `pricing.rateMap`
  // (`siteWorksMatrixGenerator.ts`'s `buildNormalizedRateMap`) instead of using
  // it as-is, so those three sections still tie out to their bills.
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = item.nettRate;
    });
    return map;
  }, [itemLibraryData]);

  // ── Drainage (Foul + Storm) ───────────────────────────────────────────────
  const drainageMatrix = useMemo(
    () =>
      buildRunMatrix({
        foul: state.foulSewers
          ? {
              pipeRuns: state.foulSewers.pipeRuns?.table ?? [],
              manholes: state.foulSewers.manholes?.table ?? [],
              laterals: state.foulSewers.lateralConnections?.table ?? [],
              eoQtys: state.foulSewers.pipeRuns?.config?.eoQtys,
              manholeScheduleConfig:
                state.foulSewers.manholes?.config?.manholeScheduleConfig,
            }
          : undefined,
        storm: state.stormSewers
          ? {
              pipeRuns: state.stormSewers.pipeRuns?.table ?? [],
              manholes: state.stormSewers.manholes?.table ?? [],
              laterals: state.stormSewers.lateralConnections?.table ?? [],
              gullies: state.stormSewers.gullyConnections?.table ?? [],
              eoQtys: state.stormSewers.pipeRuns?.config?.eoQtys,
              manholeScheduleConfig:
                state.stormSewers.manholes?.config?.manholeScheduleConfig,
            }
          : undefined,
        rateMap,
        // Foul/Storm Bill Summary now apply the same OH&P/Preliminaries markup
        // every other R&S Bill Summary does, and so does Sum Analysis's
        // foulTotal/stormTotal — the drainage matrix must mark up the same way
        // or it would disagree with Sum Analysis by exactly that markup.
        markupFactor,
      }),
    [state.foulSewers, state.stormSewers, rateMap, markupFactor],
  );

  // ── Everything else (Preliminaries → Spoil, plus the two build-up sub-tabs) ─
  const siteWorks = useMemo(
    () =>
      buildSiteWorksMatrices({
        prelimsItems: state.preliminaryItems?.preliminaryItems,
        roads: state.roads,
        mainServicesTable: state.mainServices?.table,
        substationTable: state.substation?.table,
        pos: state.pos,
        compound: state.compound,
        spoil: {
          roads: state.roads,
          foul: {
            pipeRuns: state.foulSewers?.pipeRuns.table ?? [],
            manholes: state.foulSewers?.manholes.table ?? [],
            laterals: state.foulSewers?.lateralConnections.table ?? [],
          },
          storm: {
            pipeRuns: state.stormSewers?.pipeRuns.table ?? [],
            manholes: state.stormSewers?.manholes.table ?? [],
            laterals: state.stormSewers?.lateralConnections.table ?? [],
            gullies: state.stormSewers?.gullyConnections.table ?? [],
          },
          pondsSwalesTable: state.pondsSwales?.table,
          inputs: {
            ...emptySpoilUserInputs(),
            ...(state.spoilReconciliation?.config ?? {}),
          },
        },
        pumpStation: state.pumpStation,
        foulLadders: state.foulSewers?.manholes?.config?.manholeScheduleConfig?.ladders,
        risingMain: state.risingMain,
        stormBuildUps: {
          pondsSwales: state.pondsSwales,
          cellular: state.cellularAttenuation,
          weholite: state.weholiteAttenuation,
          conc: state.concAttenuation,
          boxCulverts: state.boxCulverts,
          // Ponds / swales row wording carries the Roads default strip depth,
          // same as StormSewersBillSummary.tsx derives it.
          exTopsoilMm: Math.round(
            (state.roads?.cutFillSections?.config?.stripValueDefault ?? 0.3) * 1000,
          ),
        },
        pricing: { rateMap, markupFactor },
      }),
    [
      state.preliminaryItems?.preliminaryItems,
      state.roads,
      state.mainServices?.table,
      state.substation?.table,
      state.pos,
      state.compound,
      state.foulSewers,
      state.stormSewers,
      state.pondsSwales,
      state.cellularAttenuation,
      state.weholiteAttenuation,
      state.concAttenuation,
      state.boxCulverts,
      state.pumpStation,
      state.foulSewers?.manholes?.config?.manholeScheduleConfig?.ladders,
      state.risingMain,
      state.spoilReconciliation?.config,
      rateMap,
      markupFactor,
    ],
  );

  // ── Dynamic Bill Summary / ad-hoc extra items mapped into the Run Matrix ──
  // Additive overlay — mirrors Plot Matrix's `applyDynamicBillSummaryToPlotMatrixRows`
  // (dynamicBillSummaryPlotMatrix.ts). Collected once here from every source
  // that can carry a `runMatrixMapping`: the page's own Dynamic Bill Summary
  // tabs, plus every fixed Bill Summary's ad-hoc extra-item bucket that offers
  // Run Matrix Mapping (see the `enableRunMatrixMapping: true` call sites in
  // PrelimsBillSummary.tsx / FoulSewersBillSummary.tsx / etc).
  const runMatrixMappedItems = useMemo(
    () =>
      resolveRunMatrixMappedItems(
        state.roadsAndSewersDynamicBillSummaryTabs ?? [],
        markupFactor,
        state.prelimsExtraItems,
        state.foulSewersExtraItems,
        state.stormSewersExtraItems,
        state.roadsExtraItems,
        state.mainServicesExtraItems,
        state.substationExtraItems,
        state.posExtraItems,
        state.compoundExtraItems,
        state.pumpStationExtraItems,
        state.risingMainExtraItems,
        state.pondsSwalesExtraItems,
        state.cellularAttenuationExtraItems,
        state.weholiteAttenuationExtraItems,
        state.concAttenuationExtraItems,
        state.boxCulvertsExtraItems,
      ),
    [
      state.roadsAndSewersDynamicBillSummaryTabs,
      markupFactor,
      state.prelimsExtraItems,
      state.foulSewersExtraItems,
      state.stormSewersExtraItems,
      state.roadsExtraItems,
      state.mainServicesExtraItems,
      state.substationExtraItems,
      state.posExtraItems,
      state.compoundExtraItems,
      state.pumpStationExtraItems,
      state.risingMainExtraItems,
      state.pondsSwalesExtraItems,
      state.cellularAttenuationExtraItems,
      state.weholiteAttenuationExtraItems,
      state.concAttenuationExtraItems,
      state.boxCulvertsExtraItems,
    ],
  );

  const mappedDrainageMatrix = useMemo(
    () => applyRunMatrixMappingToDrainage(drainageMatrix, runMatrixMappedItems),
    [drainageMatrix, runMatrixMappedItems],
  );

  const sectionMatrices = useMemo(
    () => siteWorks.sections.map((section) => applyRunMatrixMappingToSection(section, runMatrixMappedItems)),
    [siteWorks.sections, runMatrixMappedItems],
  );
  const activeMatrix = sectionMatrices.find((m) => m.key === activeSection);

  // Foul/Storm build-ups aren't in `siteWorks.sections` (they render as a
  // sub-tab under Drainage Runs, not a top-level section — see `DrainageRunMatrix.tsx`),
  // but `applyRunMatrixMappingToSection` works on any `SectionMatrix` shape and
  // both already carry the matching `key` (`foul-build-ups`/`storm-build-ups`),
  // so a "Foul Sewers — Pump Station & Rising Main"/"Storm Sewers — Ponds &
  // Attenuation" mapped item resolves here the same way.
  const mappedFoulBuildUps = useMemo(
    () => applyRunMatrixMappingToSection(siteWorks.foulBuildUps, runMatrixMappedItems),
    [siteWorks.foulBuildUps, runMatrixMappedItems],
  );
  const mappedStormBuildUps = useMemo(
    () => applyRunMatrixMappingToSection(siteWorks.stormBuildUps, runMatrixMappedItems),
    [siteWorks.stormBuildUps, runMatrixMappedItems],
  );

  const sectionOptions = [
    { label: 'Drainage Runs', value: 'drainage' as ActiveSection },
    ...sectionMatrices.map((m) => ({ label: m.title, value: m.key as ActiveSection })),
  ];

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    try {
      // Same rows the main BOQ export's "Sum Analysis" sheet shows
      // (ReviewCalculate.tsx), via the shared row builder — see the
      // "Summary" sheet doc comment in runMatrixExcelExport.ts.
      const sumAnalysisRows = buildRoadsAndSewersSumAnalysisRows(
        sumAnalysisTotals,
        state.weholiteAttenuation?.config?.supplierName ?? 'Weholite',
      );
      const discountRow =
        sumAnalysisTotals.discountAmount > 0
          ? {
              label: `DISCOUNT (${settings.roadsAndSewers.discount}%)`,
              amount: -sumAnalysisTotals.discountAmount,
            }
          : null;

      await exportRunMatrixToExcel(
        mappedDrainageMatrix,
        [...sectionMatrices, mappedFoulBuildUps, mappedStormBuildUps],
        {
          projectName: state.projectName || 'Tender',
          clientName: state.client || '',
          region: state.region || '',
        },
        {
          rows: sumAnalysisRows,
          discountRow,
          tenderTotal: sumAnalysisTotals.finalTotal,
        },
      );
      message.success('Run Matrix exported');
    } catch (error) {
      console.error('Run Matrix export failed:', error);
      message.error('Run Matrix export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title
          level={5}
          style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 16, color: '#111827' }}
        >
          Run Matrix
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Cost per unit of take-off, section by section — every Bill Summary item allocated
          to the run, road, element or area it belongs to.
        </Text>
      </div>

      {/* ── Run Matrix Total — sourced from `useRoadsAndSewersTotalSync`, the
          same hook/options `RsSumAnalysisBillSummary.tsx` renders from, so it
          ties to Sum Analysis's GRAND TOTAL exactly rather than being a
          second calculation that merely tries to match it. It is not a sum of
          the section matrices further down the page: Sum Analysis's GRAND
          TOTAL also includes any dynamic Bill Summary tabs and ad-hoc extra
          items, none of which are take-off sections with a run/road/element
          for the Run Matrix to allocate against. Deliberately excludes
          discount (Sum Analysis's TENDER TOTAL row, GRAND TOTAL minus
          discount) — a discount is a tender-level commercial adjustment, not
          a cost against any run/road/element, so it has nothing to allocate
          here and must never move this banner. ──────────────────────────*/}
      <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff', borderColor: '#adc6ff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 14 }}>
            Run Matrix Total
          </Text>
          <Space size={8}>
            <Text strong style={{ fontSize: 20, color: '#1d39c4' }}>
              {fmtCurrency(sumAnalysisGrandTotal)}
            </Text>
            <Tooltip title="Sourced from the same Bill of Quantities Summary calculation as the Sum Analysis tab's GRAND TOTAL row, so the two always match exactly. Excludes discount — if a discount is applied on Sum Analysis, this total will be higher than Sum Analysis's TENDER TOTAL row by that discount amount.">
              <Text type="secondary" style={{ fontSize: 12 }}>
                (ties to Sum Analysis)
              </Text>
            </Tooltip>
          </Space>
        </div>
      </Card>

      {/* ── Section switcher + shared controls ─────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Segmented
          value={activeSection}
          onChange={(value) => setActiveSection(value as ActiveSection)}
          options={sectionOptions}
        />
        <Space size={12} wrap>
          {(ohpPercent > 0 || preliminariesPercent > 0) && (
            <Tooltip title="Mirrors the Sum Analysis tab's markup toggles — change them there and the matrix follows. Applied to every section, matching each one's own Bill Summary tab.">
              <Space size={12}>
                {ohpPercent > 0 && (
                  <Checkbox checked={includeOhp} disabled>
                    OH&P ({ohpPercent}%)
                  </Checkbox>
                )}
                {preliminariesPercent > 0 && (
                  <Checkbox checked={includePreliminaries} disabled>
                    Preliminaries ({preliminariesPercent}%)
                  </Checkbox>
                )}
              </Space>
            </Tooltip>
          )}
          <Button icon={<QuestionCircleOutlined />} onClick={() => setAllocationModalOpen(true)}>
            How allocation works
          </Button>
          {showRnsExports && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isExporting}
              onClick={handleExport}
            >
              {isExporting ? 'Exporting…' : 'Export to Excel'}
            </Button>
          )}
        </Space>
      </div>

      {markupFactor !== 1 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Markup applied — amounts are ${
            includeOhp && includePreliminaries
              ? `OH&P ${ohpPercent}% then Preliminaries ${preliminariesPercent}%`
              : includeOhp
              ? `OH&P ${ohpPercent}%`
              : `Preliminaries ${preliminariesPercent}%`
          } above nett, same as every Bill Summary tab's own rate column, on every section of this matrix.`}
        />
      )}

      {activeSection === 'drainage' ? (
        <DrainageRunMatrix
          matrix={mappedDrainageMatrix}
          foulBuildUps={mappedFoulBuildUps}
          stormBuildUps={mappedStormBuildUps}
          loading={isLoadingItemLibrary}
        />
      ) : activeMatrix ? (
        <SectionMatrixTable matrix={activeMatrix} loading={isLoadingItemLibrary} />
      ) : null}

      {/* ── Allocation rules ───────────────────────────────────────────────── */}
      <Modal
        open={allocationModalOpen}
        onCancel={() => setAllocationModalOpen(false)}
        footer={null}
        width={780}
        title="How the Run Matrix allocates cost"
      >
        <Paragraph style={{ fontSize: 13 }}>
          Nothing is re-priced here. Each section&apos;s matrix reads that section&apos;s own
          Bill Summary items — same quantities, same item-library rates — and lays them out
          against the unit that section is taken off by.
        </Paragraph>

        <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
          <Text strong>Drainage Runs</Text> — one row per pipe run. An item lands on a run
          in one of three ways:
        </Paragraph>
        <ol style={{ fontSize: 13, paddingLeft: 20 }}>
          <li>
            <Text strong>Its own row</Text> — pipe runs, manholes, laterals and gullies are
            already per-entity items. A manhole is charged to the run it heads (its From MH),
            or to the run arriving at it if it is a terminal chamber, so each chamber is
            counted once.
          </li>
          <li>
            <Text strong>Its formula breakdown</Text> — aggregated items (rockers, junctions,
            concrete protection, manhole extras) carry the per-source breakdown shown in the
            Bill Summary&apos;s formula drawer, and are split pro-rata across those runs.
          </li>
          <li>
            <Text strong>Site-wide</Text> — manually-entered aggregate items belong to the
            scheme, not a run, and sit on the &quot;Not run-specific&quot; row. They are part
            of the total, never dropped.
          </li>
        </ol>

        <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
          <Text strong>The other sections</Text>
        </Paragraph>
        <ul style={{ fontSize: 13, paddingLeft: 20 }}>
          <li style={{ marginBottom: 6 }}>
            <Text strong>Roads</Text> and <Text strong>Main Services</Text> are measured per
            road, so the matrix is a transpose of their Bill Summary — roads down the side,
            the bill&apos;s own sections (or utilities) across the top. No re-classification
            is involved.
          </li>
          <li style={{ marginBottom: 6 }}>
            <Text strong>POS</Text> uses one row per public open space and the take-off&apos;s
            own path sub-headings as columns.
          </li>
          <li style={{ marginBottom: 6 }}>
            <Text strong>Preliminaries</Text> has one row per bill group, with columns by
            charge type read off each row&apos;s own unit — so fixed lump sums, weekly
            time-related costs and enumerated items separate out. Rates are the
            estimator&apos;s own entered rate where there is one, otherwise the item-library
            rate.
          </li>
          <li style={{ marginBottom: 6 }}>
            <Text strong>The Foul and Storm build-ups</Text> (Pump Station, Rising Main;
            Ponds &amp; Swales, Cellular / Weholite / Conc attenuation, Box Culverts) sit as
            a sub-tab under their own discipline in Drainage Runs, with one row per element,
            tank, pond or culvert and items grouped into work categories.
          </li>
          <li style={{ marginBottom: 6 }}>
            <Text strong>Substation</Text> and <Text strong>Compound</Text> have no second
            axis of their own, so their items are grouped into work categories (earthworks,
            sub-base, concrete, surfacing, kerbs, services, landscaping, furniture, sundries)
            by matching keywords in each description. That can put an item in a neighbouring
            column, but never loses one — unmatched items go to Sundries, row and section
            totals are unaffected, and every cell opens to show exactly what it holds.
          </li>
          <li style={{ marginBottom: 6 }}>
            <Text strong>Spoil Reconciliation</Text> has no rates at all — it is a volume
            balance, so that matrix is in m³: topsoil strip / re-use / surplus and subsoil
            cut / fill / surplus per area, with net surplus as the row total.
          </li>
        </ul>

        <Paragraph style={{ fontSize: 13 }}>
          Measured items with no item-library rate carry £0 and are counted separately in
          each section&apos;s header, so a column reading zero is never ambiguous — open the
          cell to see the measured quantities behind it.
        </Paragraph>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 8 }}
          message="Coverage"
          description="Every priced section of the tender now has a matrix: Preliminaries, Foul (runs + Pump Station / Rising Main), Storm (runs + ponds, attenuation and culverts), Roads, Main Services, Substation, POS and Compound, plus Spoil Reconciliation as volumes. Sum Analysis is a roll-up of those sections rather than a section of its own, so it has no matrix."
        />

        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 13 }}>
            Reconciliation against the Bill Summary
          </Text>
          <Table
            style={{ marginTop: 8 }}
            size="small"
            bordered
            pagination={false}
            rowKey="section"
            dataSource={[
              ...(['foul', 'storm'] as RunMatrixDiscipline[])
                .filter(
                  (d) =>
                    mappedDrainageMatrix.reconciliation[d].billTotal !== 0 ||
                    mappedDrainageMatrix.disciplineTotals[d] !== 0,
                )
                .map((d) => ({
                  section: RUN_MATRIX_DISCIPLINE_LABEL[d],
                  billTotal: mappedDrainageMatrix.reconciliation[d].billTotal,
                  matrixTotal: mappedDrainageMatrix.disciplineTotals[d],
                  variance: mappedDrainageMatrix.reconciliation[d].variance,
                })),
              ...sectionMatrices
                .filter((m) => m.valueKind === 'currency')
                .map((m) => ({
                  section: m.title,
                  billTotal: m.billTotal,
                  matrixTotal: m.grandTotal,
                  variance: m.variance,
                })),
            ]}
            columns={[
              { title: 'Section', dataIndex: 'section', key: 'section' },
              {
                title: 'Bill Summary',
                dataIndex: 'billTotal',
                key: 'billTotal',
                align: 'right',
                render: (v: number) => fmtCurrency(v),
              },
              {
                title: 'Matrix',
                dataIndex: 'matrixTotal',
                key: 'matrixTotal',
                align: 'right',
                render: (v: number) => fmtCurrency(v),
              },
              {
                title: 'Variance',
                dataIndex: 'variance',
                key: 'variance',
                align: 'right',
                render: (v: number) => (
                  <Text type={Math.abs(v) < 0.01 ? 'success' : 'danger'}>{fmtCurrency(v)}</Text>
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RunMatrix;
